import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft, KeyRound, CheckCircle2, Mail, Loader2 } from "lucide-react";
import { BrandLogo } from "@/projects/components/brand-logo";
import { Button } from "@/projects/components/ui/button";
import { Input } from "@/projects/components/ui/input";
import { Label } from "@/projects/components/ui/label";
import { Card } from "@/projects/components/ui/card";
import { axiosConfig } from "@/services/axiosConfig";
import { toast } from "@/projects/lib/toast";

export const Route = createFileRoute("/forgot-password")({
  head: () => ({
    meta: [
      { title: "Forgot Password — રાજનગર જ્ઞાનોત્સવ 2.0" },
      {
        name: "description",
        content: "Reset your Rajnagar Gyanotsav 2.0 account password.",
      },
    ],
  }),
  component: ForgotPasswordPage,
});

function getForgotPasswordErrorMessage(error: unknown): string {
  const err = error as {
    response?: { data?: Record<string, unknown> };
    message?: string;
  };
  const data = err?.response?.data;
  if (data) {
    for (const key of ["message", "detail", "error", "email"]) {
      const v = data[key];
      if (typeof v === "string" && v.trim()) {
        return v;
      }
      if (Array.isArray(v) && typeof v[0] === "string") {
        return v[0];
      }
    }
  }
  return err?.message || "Failed to send reset link. Please try again.";
}

function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "teacher">("teacher");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error("Please enter your email or username");
      return;
    }

    setLoading(true);
    try {
      // POST /forgot-password-request/?is_rajnagar=true  { email }
      await axiosConfig.post(
        "/forgot-password-request/",
        { email: trimmed },
        {
          params: { is_rajnagar: true },
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      setSent(true);
      toast.success("Reset instructions sent successfully.");
    } catch (error) {
      toast.error(getForgotPasswordErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-background relative flex min-h-screen items-center justify-center overflow-hidden p-4">
      <div className="gradient-hero absolute inset-0 opacity-95" />
      <div className="motif-bg absolute inset-0 opacity-50" />

      <div className="relative w-full max-w-md">
        <Link
          to="/"
          className="mb-4 inline-flex items-center gap-2 text-sm text-white/90 hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>

        <Card className="shadow-card p-8">
          <div className="mb-6 flex items-center gap-3">
            <BrandLogo className="h-11 w-11" />
            <div>
              <div className="text-guj text-sm font-bold">
                રાજનગર જ્ઞાનોત્સવ 2.0
              </div>
              <div className="text-muted-foreground text-[10px] font-medium tracking-widest uppercase">
                Account Recovery
              </div>
            </div>
          </div>

          {!sent ? (
            <>
              <div className="border-saffron/40 bg-saffron-soft text-saffron inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium">
                <KeyRound className="h-3.5 w-3.5" /> Forgot Password
              </div>
              <h1 className="mt-3 text-2xl font-bold">Reset your password</h1>
              <p className="text-muted-foreground mt-1 text-sm">
                Enter your account details and we&apos;ll send you a reset link.
              </p>

              <form
                onSubmit={e => void handleSubmit(e)}
                className="mt-6 space-y-4"
              >
                <div>
                  <Label>Account Type</Label>
                  <div className="mt-1.5 grid grid-cols-2 gap-2">
                    {(["teacher", "admin"] as const).map(r => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setRole(r)}
                        className={`rounded-md border px-3 py-2 text-sm font-medium capitalize transition-colors ${role === r ? "border-royal bg-royal-soft text-royal" : "hover:bg-muted"}`}
                      >
                        {r === "admin" ? "Super Admin" : "Teacher"}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email or Username</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    disabled={loading}
                    placeholder={
                      role === "admin"
                        ? "admin@rajnagar.edu"
                        : "your.email@school.edu"
                    }
                  />
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    "Send reset link"
                  )}
                </Button>
              </form>

              <div className="text-muted-foreground mt-6 flex items-center justify-between text-xs">
                <Link to="/login/teacher" className="hover:text-foreground">
                  ← Teacher login
                </Link>
                <Link to="/login/admin" className="hover:text-foreground">
                  Admin login →
                </Link>
              </div>
            </>
          ) : (
            <div className="py-6 text-center">
              <div className="bg-success/10 text-success mx-auto grid h-14 w-14 place-items-center rounded-full">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <h1 className="mt-4 text-xl font-bold">Check your inbox</h1>
              <p className="text-muted-foreground mt-2 text-sm">
                If an account exists for{" "}
                <span className="text-foreground font-medium">{email}</span>,
                we&apos;ve sent password reset instructions to it.
              </p>
              <div className="bg-muted/40 mt-5 rounded-lg border p-3 text-left text-xs">
                <div className="text-foreground flex items-center gap-2 font-medium">
                  <Mail className="h-3.5 w-3.5" /> Next steps
                </div>
                <div className="text-muted-foreground mt-1">
                  Check your inbox for reset instructions, then sign in again
                  with your email and new password.
                </div>
              </div>
              <div className="mt-6 flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setSent(false);
                    setEmail("");
                  }}
                >
                  Try another email
                </Button>
                <Button
                  className="flex-1"
                  onClick={() =>
                    navigate({
                      to: role === "admin" ? "/login/admin" : "/login/teacher",
                    })
                  }
                >
                  Go to login
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
